"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Search, X } from "lucide-react";
import { GRADES, SUBJECTS, DOC_TYPES, SORT_OPTIONS } from "@/lib/taxonomy";
import { cn } from "@/lib/cn";

export interface FilterState {
  q?: string;
  grade?: string;
  subject?: string;
  type?: string;
  accessType?: string;
  source?: string;
  sort?: string;
}

const selectClass =
  "h-10 rounded-md border border-border-strong bg-surface px-3 text-body-sm text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:border-primary";

export function DocumentFilters({
  current,
  hideSource = false,
  hideAccessType = false,
  basePath = "/tai-lieu",
}: {
  current: FilterState;
  hideSource?: boolean;
  hideAccessType?: boolean;
  basePath?: string;
}) {
  const router = useRouter();
  const [q, setQ] = React.useState(current.q ?? "");

  function navigate(next: FilterState) {
    const params = new URLSearchParams();
    const merged = { ...current, ...next };
    for (const [key, value] of Object.entries(merged)) {
      if (value && key !== "page") params.set(key, value);
    }
    const qs = params.toString();
    router.push(qs ? `${basePath}?${qs}` : basePath);
  }

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    navigate({ q: q.trim() || undefined });
  }

  const hasActive =
    current.q ||
    current.grade ||
    current.subject ||
    current.type ||
    current.accessType ||
    current.source;

  return (
    <div className="space-y-3">
      <form onSubmit={onSearchSubmit} className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tìm giáo án, phiếu bài tập, đề kiểm tra…"
          aria-label="Tìm tài liệu"
          className="h-12 w-full rounded-lg border border-border-strong bg-surface pl-11 pr-3 text-body text-foreground placeholder:text-muted focus-visible:outline-none focus-visible:ring focus-visible:ring-ring focus-visible:border-primary"
        />
      </form>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className={selectClass}
          value={current.grade ?? "all"}
          onChange={(e) => navigate({ grade: e.target.value })}
          aria-label="Khối lớp"
        >
          <option value="all">Tất cả khối</option>
          {GRADES.map((g) => (
            <option key={g} value={String(g)}>
              Lớp {g}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          value={current.subject ?? ""}
          onChange={(e) => navigate({ subject: e.target.value || undefined })}
          aria-label="Môn học"
        >
          <option value="">Tất cả môn</option>
          {SUBJECTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          value={current.type ?? ""}
          onChange={(e) => navigate({ type: e.target.value || undefined })}
          aria-label="Loại tài liệu"
        >
          <option value="">Tất cả loại</option>
          {DOC_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {!hideAccessType && (
          <select
            className={selectClass}
            value={current.accessType ?? ""}
            onChange={(e) => navigate({ accessType: e.target.value || undefined })}
            aria-label="Quyền truy cập"
          >
            <option value="">Mọi quyền truy cập</option>
            <option value="free">Miễn phí</option>
            <option value="vip">Dành cho VIP</option>
            <option value="paid">Có trả phí</option>
          </select>
        )}

        {!hideSource && (
          <select
            className={selectClass}
            value={current.source ?? ""}
            onChange={(e) => navigate({ source: e.target.value || undefined })}
            aria-label="Nguồn tài liệu"
          >
            <option value="">Mọi nguồn</option>
            <option value="system">Từ nền tảng</option>
            <option value="community">Từ giáo viên</option>
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <label className="text-body-sm text-muted" htmlFor="sort">
            Sắp xếp
          </label>
          <select
            id="sort"
            className={selectClass}
            value={current.sort ?? "newest"}
            onChange={(e) => navigate({ sort: e.target.value })}
            aria-label="Sắp xếp"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {hasActive && (
          <button
            type="button"
            onClick={() => router.push(basePath)}
            className={cn(
              "inline-flex h-10 items-center gap-1 rounded-md px-3 text-body-sm text-muted hover:bg-subtle hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
            )}
          >
            <X className="size-4" aria-hidden="true" />
            Xóa lọc
          </button>
        )}
      </div>
    </div>
  );
}
